import React, { useEffect, useState } from "react";
import { LikeButton } from "@/components/LikeButton";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Spinner } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";

const PAGE = 6;
export const fmtDate = (d: string) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");
export const postMeta = (p: any) => [fmtDate(p.date), p.category, p.read_min && `${p.read_min} min read`].filter(Boolean).join(" | ");

function Featured({ p }: any) {
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-8 pt-14 md:pt-16 grid md:grid-cols-2 gap-10 md:gap-16 items-center" data-testid="blog-featured">
      <Link to={`/blog/${p.slug}`} className="block overflow-hidden"><img src={p.image} alt={p.title} className="w-full aspect-[5/3] object-cover hover:scale-[1.02] transition-transform duration-700" /></Link>
      <div className="text-center px-2 md:px-10">
        <h2 className="font-display uppercase text-3xl md:text-[2.4rem] leading-tight tracking-[0.02em]">{p.title}</h2>
        <p className="mt-6 text-[15px] leading-7 text-neutral-800 max-w-lg mx-auto">{p.excerpt}</p>
        <Link to={`/blog/${p.slug}`} className="inline-block mt-8 border border-neutral-900 rounded-full px-14 py-3 text-[13px] font-medium tracking-wide uppercase hover:bg-neutral-900 hover:text-white transition-colors" data-testid="blog-featured-read-more">Read More</Link>
      </div>
    </section>
  );
}

export function BlogCard({ p }: any) {
  return (
    <article className="group" data-testid={`blog-card-${p.slug}`}>
      <Link to={`/blog/${p.slug}`} className="block overflow-hidden bg-neutral-100"><img src={p.image} alt={p.title} loading="lazy" className="w-full aspect-[5/2] object-cover transition-transform duration-700 group-hover:scale-105" /></Link>
      <Link to={`/blog/${p.slug}`}><h3 className="font-display text-[1.75rem] md:text-[2rem] leading-tight mt-6 line-clamp-3 group-hover:text-[var(--c-primary)] transition-colors">{p.title}</h3></Link>
      <div className="mt-4 flex items-center justify-between gap-4"><p className="text-sm tracking-wide text-neutral-800" data-testid="blog-card-meta">{postMeta(p)}</p><LikeButton slug={p.slug} likes={p.likes || 0} size="sm" /></div>
      <p className="mt-4 text-[15px] leading-7 text-neutral-800 line-clamp-4">{p.excerpt}</p>
      <Link to={`/blog/${p.slug}`} className="inline-block mt-5 text-[15px] font-medium uppercase tracking-wide underline underline-offset-4 hover:text-[var(--c-primary)]" data-testid={`blog-card-read-more-${p.slug}`}>Read More</Link>
    </article>
  );
}

const SORTS: [string, string][] = [["newest", "Newest first"], ["oldest", "Oldest first"], ["az", "Title A–Z"], ["za", "Title Z–A"],
  ["category", "Category A–Z"], ["loved", "Most loved"], ["shared", "Most shared"], ["quick", "Quickest read"], ["long", "Longest read"]];
const shareSum = (p: any): number => Object.values(p.shares || {}).reduce((s: number, n: any) => s + (Number(n) || 0), 0) as number;
const byDate = (a: any, b: any) => (b.date || "").localeCompare(a.date || "");
const byTitle = (a: any, b: any) => (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" });
function sortPosts(list: any[], k: string) {
  const l = [...list];
  const cmp: Record<string, (a: any, b: any) => number> = {
    newest: byDate, oldest: (a, b) => -byDate(a, b), az: byTitle, za: (a, b) => -byTitle(a, b),
    category: (a, b) => (a.category || "").localeCompare(b.category || "") || byDate(a, b), loved: (a, b) => (b.likes || 0) - (a.likes || 0) || byDate(a, b), shared: (a, b) => shareSum(b) - shareSum(a) || (b.likes || 0) - (a.likes || 0) || byDate(a, b),
    quick: (a, b) => (a.read_min || 0) - (b.read_min || 0) || byDate(a, b), long: (a, b) => (b.read_min || 0) - (a.read_min || 0) || byDate(a, b),
  };
  return l.sort(cmp[k] || byDate);
}

function TagCloud({ posts, catsOf, active, onPick }: { posts: any[]; catsOf: (p: any) => string[]; active: string; onPick: (c: string) => void }) {
  const counts: Record<string, number> = {};
  posts.forEach((p) => catsOf(p).forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
  const tags = Object.entries(counts).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="blog-tag-cloud">
      {tags.map(([c, n]) => (
        <button key={c} onClick={() => onPick(c)} data-testid={`blog-tag-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`rounded-full border px-4 py-1.5 text-xs tracking-[0.08em] transition-colors ${active === c ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 text-neutral-700 hover:border-neutral-900"}`}>
          {c} <span className={active === c ? "text-white/70" : "text-neutral-400"}>{n}</span>
        </button>
      ))}
    </div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState<any[] | null>(null);
  const [params, setParams] = useSearchParams();
  const [shown, setShown] = useState(PAGE);
  const catKey = (params.get("category") || "All").toLowerCase();
  useMeta("Journal | Somnio.Co", "Stories, guides and inspiration from the Somnio.Co atelier.");
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);
  useEffect(() => setShown(PAGE), [catKey]);

  if (!posts) return <Spinner />;
  const catsOf = (p: any): string[] => (p.categories?.length ? p.categories : [p.category]).filter(Boolean);
  const cats = ["All", ...Array.from(new Set(posts.flatMap(catsOf))).sort()];
  const catParam = params.get("category") || "All";
  const cat = cats.find((c) => c.toLowerCase() === catParam.toLowerCase()) || catParam;
  const list = cat === "All" ? posts : posts.filter((p) => catsOf(p).includes(cat));
  const featured = list.find((p) => p.featured);
  const sortKey = SORTS.some((s) => s[0] === params.get("sort")) ? (params.get("sort") as string) : "newest";
  const rest = sortPosts(list.filter((p) => p !== featured), sortKey);
  const setSort = (v: string) => { const n = new URLSearchParams(params); if (v === "newest") n.delete("sort"); else n.set("sort", v); setParams(n); };
  return (
    <div data-testid="blog-page">
      <nav className="bg-[#EFEBE4]" data-testid="blog-category-bar">
        <div className="max-w-[1400px] mx-auto px-5 md:px-24 py-6 flex gap-4 md:gap-5 overflow-x-auto">
          {cats.map((c) => (
            <button key={c} onClick={() => { const n = new URLSearchParams(params); if (c === "All") n.delete("category"); else n.set("category", c); setParams(n); }} data-testid={`blog-cat-${c.toLowerCase()}`}
              className={`flex-none border px-4 md:px-4 py-7 min-w-[56px] text-[14px] font-medium uppercase tracking-[0.06em] transition-colors ${cat === c ? "border-neutral-900 bg-white" : "border-white/80 hover:border-neutral-400"}`}>{c}</button>
          ))}
        </div>
      </nav>
      {featured && <Featured p={featured} />}
      <section className="max-w-[1400px] mx-auto px-5 md:px-8 pt-16 md:pt-20">
        <h1 className="font-display uppercase text-3xl md:text-[2.4rem] text-center tracking-[0.04em] mb-6">{cat === "All" ? "Latest Stories" : cat}</h1>
        <TagCloud posts={posts} catsOf={catsOf} active={cat} onPick={(c) => { const n = new URLSearchParams(params); if (c === cat) n.delete("category"); else n.set("category", c); setParams(n); }} />
        <div className="flex justify-end mb-8">
          <label className="flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-neutral-600">Sort by
            <select value={sortKey} onChange={(e) => setSort(e.target.value)} className="border border-neutral-300 bg-white px-3 py-2 text-sm normal-case tracking-normal text-neutral-900" data-testid="blog-sort-select">
              {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
        </div>
        {rest.length === 0 && <p className="text-center text-neutral-500 py-16" data-testid="blog-empty">No stories in this category yet.</p>}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-16" data-testid="blog-grid">
          {rest.slice(0, shown).map((p) => <BlogCard key={p.id} p={p} />)}
        </div>
        <div className="text-center mt-16">
          {shown < rest.length && <button onClick={() => setShown(shown + PAGE)} className="border border-neutral-900 rounded-full px-14 py-3 text-[13px] font-medium uppercase hover:bg-neutral-900 hover:text-white transition-colors" data-testid="blog-load-more">Load More</button>}
          <p className="mt-6 text-sm text-neutral-500" data-testid="blog-showing-count">Showing {Math.min(shown, rest.length)} of {rest.length} Posts</p>
        </div>
      </section>
    </div>
  );
}
